import { formatEt } from "../User/introPauseUtils";

/** 24-char hex Mongo ObjectId */
export const isLikelyObjectId = (value) =>
  typeof value === "string" && /^[a-f\d]{24}$/i.test(value);

/**
 * Extracts the single interaction/compatibility document from GET /admin/interactions/:id
 * (handles { status, data }, nested interaction, or raw document).
 */
export function extractRecordFromInteractionResponse(res) {
  const b = res?.data;
  if (!b || typeof b !== "object") return null;

  const d = b.data;
  if (d && typeof d === "object" && !Array.isArray(d)) {
    if (d._id) return d;
    if (d.interaction && typeof d.interaction === "object" && d.interaction._id) {
      return d.interaction;
    }
    if (d.compatibility && typeof d.compatibility === "object" && d.compatibility._id) {
      return d.compatibility;
    }
  }
  if (b._id) return b;
  return null;
}

/**
 * Last resort when a member has neither an email nor a name.
 *
 * 🔴 NEVER RETURN A BARE OBJECTID. A raw 24-character id printed in a column of
 * email addresses reads as corruption: the founder saw one in the Introductions
 * table and asked whether it was an Apple private relay address. It is our own
 * database key and means nothing to an operator. A labelled short form still
 * lets them search for the record without pretending to be an identity.
 */
function idFallback(id) {
  if (id == null) return "";
  const s = String(id);
  return s.length >= 8 ? `no email (${s.slice(-8)})` : s;
}

/** Same resolution rules as CompatibilityListLayer `getFromDisplay` / `getToDisplay` */
export function resolveFromUserDisplay(r) {
  if (!r) return "N/A";
  if (typeof r.fromUser === "string" && r.fromUser.trim()) return r.fromUser.trim();
  if (r.fromUser && typeof r.fromUser === "object") {
    const v =
      r.fromUser.email ||
      r.fromUser.fullName ||
      idFallback(r.fromUser._id);
    if (v) return v;
  }
  if (r.userA != null) {
    if (typeof r.userA === "string" && r.userA.trim()) return r.userA.trim();
    if (typeof r.userA === "object") {
      const v =
        r.userA.email ||
        r.userA.fullName ||
        idFallback(r.userA._id);
      if (v) return v;
    }
  }
  return "N/A";
}

export function resolveToUserDisplay(r) {
  if (!r) return "N/A";
  if (typeof r.toUser === "string" && r.toUser.trim()) return r.toUser.trim();
  if (r.toUser && typeof r.toUser === "object") {
    const v =
      r.toUser.email ||
      r.toUser.fullName ||
      idFallback(r.toUser._id);
    if (v) return v;
  }
  if (r.userB != null) {
    if (typeof r.userB === "string" && r.userB.trim()) return r.userB.trim();
    if (typeof r.userB === "object") {
      const v =
        r.userB.email ||
        r.userB.fullName ||
        idFallback(r.userB._id);
      if (v) return v;
    }
  }
  return "N/A";
}

const idOf = (v) => (v && typeof v === "object" ? v._id : v);

/** A closed row whose endReason is "paused" (a pair brought back is not). */
const isPauseClose = (r) => {
  if (!r || r.endReason !== "paused") return false;
  const status = r.compatibilityStatus || r.status;
  return !status || status === "expired";
};

/** The first name (or email) of the side who paused, or "" when no field names one. */
const pauserNameOf = (r) => {
  const sides = [r.fromUser || r.userA, r.toUser || r.userB].filter((u) => u && typeof u === "object");
  const pauserId = [r.pausedBy, r.closedBy, r.endedBy].map(idOf).find(
    (id) => id != null && sides.some((u) => String(u._id) === String(id))
  );
  const pauser = pauserId != null ? sides.find((u) => String(u._id) === String(pauserId)) : null;
  return pauser ? String(pauser.fullName || "").trim().split(/\s+/)[0] || pauser.email || "" : "";
};

/**
 * "Closed: Kayleigh took a break" for an unrevealed introduction closed
 * because one of the two paused their introductions (endReason "paused",
 * founder go 2026-09-27). null for any other row.
 *
 * WHO paused. A pause close leaves endedBy empty on purpose (a pause is not
 * an End), and it stays empty even when a quick resume later counts the
 * close as their End (pauseCountedAsEndAt, see pauseCountedAsEndLine). So
 * the pauser is read from the first field that names one of the two sides:
 * pausedBy, then closedBy, then endedBy. When none does, the label says "one
 * of them" rather than guess.
 *
 * Only on a closed row: a pair brought back later is not "Closed", whatever
 * its old endReason says.
 */
export function pausedCloseLabel(r) {
  if (!isPauseClose(r)) return null;
  const first = pauserNameOf(r);
  return first ? `Closed: ${first} took a break` : "Closed: one of them took a break";
}

/**
 * "Counted as Kayleigh's own End (resumed within 24 hours, Sep 28, 10:02 AM
 * ET)" when the one who paused came back within 24 hours of the pause that
 * closed this card (founder decision 2026-09-27). The server then stamps
 * pauseCountedAsEndAt (the resume's moment) and the close counts as their
 * End for the wait rules and the End's silent-partner lapse; endedBy stays
 * empty. null for any other row, or a row the server sent without the field.
 */
export function pauseCountedAsEndLine(r) {
  if (!isPauseClose(r) || !r.pauseCountedAsEndAt) return null;
  const first = pauserNameOf(r);
  const when = formatEt(r.pauseCountedAsEndAt);
  return (
    `Counted as ${first ? `${first}'s own End` : "the End of the one who paused"}` +
    ` (resumed within 24 hours${when ? `, ${when}` : ""})`
  );
}

// ------------------------------------------------------------------
// The ending and the re-deal (founder go 2026-10-04). The server adds
// endedAt, endAnswers, earlierEnds and introducedAgain to each row of
// GET /admin/interactions (backend utils/introductionsHistory). These word
// them once, so the table and its tests say the same thing. Times are
// always Eastern (formatEt). Admin copy rules too: no dashes, never "match".
// ------------------------------------------------------------------

// End Compatibility reason chips (v2.0 confirm sheet; server enum in
// endCompatibilitySession). Older clients send none, so most rows are bare.
// Moved here from CompatibilityListLayer (2026-10-20) so the old table and
// the Introductions board say the same words.
export const END_REASON_LABELS = {
  not_feeling_it: "Not feeling it",
  not_the_right_time: "Not the right time",
  too_far_away: "Too far away",
  curious_whos_next: "Curious who's next",
  prefer_not_to_say: "Prefer not to say",
  // Machine reasons that land in endReason (no person tapped End), in words
  // (founder 2026-09-24): the row used to print the raw code.
  daily_expired: "Nobody opened it in a day",
  quiet_auto: "Went quiet, no reveal",
  quiet_mutual: "Both went quiet",
  quiet_stalled: "Conversation went quiet",
  partner_left: "The other member left",
  // One of them paused their introductions (2026-09-27). The status line
  // names who ("Closed: {First} took a break"); this is the chip's word.
  paused: "Took a break",
};

// Machine-close reasons (closeReason, not endReason): the system ended the
// pair, no person tapped End. Shown so a blank row never looks like a mystery.
export const CLOSE_REASON_LABELS = {
  partner_left: "The other member left",
  quiet_mutual: "Both went quiet",
  quiet_stalled: "Conversation went quiet",
  quiet_auto: "Went quiet, no reveal",
};

const msOf = (value) => {
  if (!value) return null;
  const t = new Date(value).getTime();
  return Number.isFinite(t) ? t : null;
};

/** "45 sec", "4 min", "3 h", "2 days" from one moment to a later one; "" otherwise. */
export function gapLabel(fromValue, toValue) {
  const a = msOf(fromValue);
  const b = msOf(toValue);
  if (a === null || b === null || b < a) return "";
  const sec = Math.round((b - a) / 1000);
  if (sec < 60) return `${sec} sec`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min} min`;
  const h = Math.round(min / 60);
  if (h < 48) return `${h} h`;
  const days = Math.round(h / 24);
  return `${days} day${days === 1 ? "" : "s"}`;
}

/** The email (else the name) of the row's member with this id; "" when neither side is it. */
export function memberLabelOf(r, id) {
  if (!r || id == null) return "";
  const sides = [r.fromUser || r.userA, r.toUser || r.userB].filter((u) => u && typeof u === "object");
  const hit = sides.find((u) => String(u._id) === String(idOf(id)));
  return hit ? hit.email || String(hit.fullName || "").trim() || "" : "";
}

/**
 * When the introduction ended, for the Dealt / Ended column:
 * { when: "Ended Oct 4, 3:12 PM ET", gap: "4 min after dealt" }, or null for
 * a row that has not ended (or a server that sent no endedAt).
 */
export function endedLines(r) {
  if (!r || !r.endedAt) return null;
  const when = formatEt(r.endedAt);
  if (!when) return null;
  const gap = gapLabel(r.createdAt, r.endedAt);
  return { when: `Ended ${when}`, gap: gap ? `${gap} after dealt` : "" };
}

const answersPair = (ender, other) =>
  Number.isFinite(ender) && Number.isFinite(other) ? `${ender} of 9 / ${other} of 9` : "";

/**
 * "At End: ender 0 of 9 · other 9 of 9" for a member End: each side's Vault
 * answers at that moment (stored at the End since 2026-10-04, otherwise
 * counted from the answers' own times). null for anything else.
 */
export function endAnswersLine(r) {
  const a = r && r.endAnswers;
  if (!a || !Number.isFinite(a.ender) || !Number.isFinite(a.other)) return null;
  return `At End: ender ${a.ender} of 9 · other ${a.other} of 9`;
}

const ordinal = (n) => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};

/**
 * "Introduced again" when these two were dealt to each other before (a
 * re-deal reuses the row and starts it over, so without this it read as a
 * brand new pair), "Introduced again (3rd time)" from the third deal. null
 * for a pair dealt once. A Reignite is not a re-deal.
 */
export function introducedAgainText(r) {
  const n = r && r.introducedAgain && r.introducedAgain.count;
  if (!Number.isFinite(n) || n < 2) return null;
  return n === 2 ? "Introduced again" : `Introduced again (${ordinal(n)} time)`;
}

/**
 * One line per member End from an earlier life of the row (or one a
 * Reignite undid): "Earlier End Sep 20, 3:12 PM ET by a@example.com,
 * Prefer not to say, 0 of 9 / 9 of 9". `labelFor` turns a reason code into
 * its chip label. `whoOf(r, id)` names the member who ended it: the email
 * by default (the old table); the Introductions board passes its first-name
 * lookup, since its panel lines name people by first name only (review
 * 2026-10-20: the board's panel read "by anya.k@gmail.com").
 */
export function earlierEndLines(r, labelFor = (x) => x, whoOf = memberLabelOf) {
  const list = r && Array.isArray(r.earlierEnds) ? r.earlierEnds : [];
  return list.map((e) => {
    const bits = [`Earlier End ${formatEt(e.at) || "(time unknown)"}`];
    const who = whoOf(r, e.by);
    if (who) bits[0] += ` by ${who}`;
    if (e.reason) bits.push(labelFor(e.reason) || e.reason);
    const counts = answersPair(e.ender, e.other);
    if (counts) bits.push(counts);
    return bits.join(", ") + (e.undone ? " (undone by a Reignite)" : "");
  });
}

/** "3 of 9" Vault answers in, as visible text (the table hides it in a hover). */
export function vaultCountText(n) {
  return `${Number.isFinite(n) ? n : 0} of 9`;
}

/**
 * The ending lines of a phone card (under 768px), in order, as visible text:
 * a title tooltip does nothing on a touch screen. "Ended Oct 3, 7:14 PM ET,
 * 4 min after dealt, by a@example.com", then "At End: ender 0 of 9 · other
 * 9 of 9", then one line per earlier End. Empty for a row that has not
 * ended and was never ended before. `labelFor` turns a reason code into its
 * chip label.
 */
export function phoneEndLines(r, labelFor = (x) => x) {
  if (!r) return [];
  const out = [];
  const ended = endedLines(r);
  if (ended) {
    let line = ended.when;
    if (ended.gap) line += `, ${ended.gap}`;
    const who = r.compatibilityStatus === "expired" && r.endedBy ? memberLabelOf(r, r.endedBy) : "";
    if (who) line += `, by ${who}`;
    out.push(line);
  }
  const answers = endAnswersLine(r);
  if (answers) out.push(answers);
  return out.concat(earlierEndLines(r, labelFor));
}

// ------------------------------------------------------------------
// "Waiting on" (founder go 2026-10-20). The server adds row.waitingOn to
// each live row of GET /admin/interactions (v 1; absent on older servers and
// on ended rows, and then nothing new is drawn). It says WHO the pair is
// waiting on and what for, the facts behind it (Vault counts, who tapped
// Reveal, who opened it, last in the app, hellos and words after the
// reveal) and what the machine does next. This words it once, so the table,
// the phone card and the tests all say the same thing.
//
// Words follow the approved mock-up: Eastern times with day words ("today
// 10:50 AM", "tonight 6 PM", "Mon 3:00 PM"), first names only, no dashes,
// never "match". Tone: warning (amber) = someone is waited on, success
// (green) = talking, neutral (grey) = gone quiet, danger = a member has left.
// ------------------------------------------------------------------

const ET_ZONE = "America/Toronto";
let etNumericFmt = null;
let etNamesFmt = null;

const etPartsOf = (fmt, ms) =>
  fmt.formatToParts(new Date(ms)).reduce((o, p) => {
    o[p.type] = p.value;
    return o;
  }, {});

/**
 * The Eastern wall clock of one moment, built from parts (not from
 * toLocaleString, whose space before AM/PM is a narrow no-break space on
 * newer ICU and broke string compares).
 */
const etMomentOf = (ms) => {
  if (!etNumericFmt) {
    etNumericFmt = new Intl.DateTimeFormat("en-US", {
      timeZone: ET_ZONE,
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
    etNamesFmt = new Intl.DateTimeFormat("en-US", { timeZone: ET_ZONE, weekday: "short", month: "short" });
  }
  const a = etPartsOf(etNumericFmt, ms);
  const b = etPartsOf(etNamesFmt, ms);
  const period = String(a.dayPeriod || "").toUpperCase();
  const hour = Number(a.hour);
  return {
    dayIndex: Math.round(Date.UTC(Number(a.year), Number(a.month) - 1, Number(a.day)) / 86400000),
    day: Number(a.day),
    hour,
    hour24: (hour % 12) + (period === "PM" ? 12 : 0),
    minute: a.minute,
    period,
    weekday: b.weekday,
    month: b.month,
  };
};

/**
 * One moment in Eastern with a day word, relative to `now`:
 * "today 10:50 AM", "Mon 3:00 PM" (within six days either way, so a weekday
 * never means two dates), else "Oct 3, 3:00 PM". `sched` is for something
 * the machine will do: an evening later today reads "tonight", and a time
 * on the hour drops its ":00" ("tonight 6 PM", "Wed 6 PM"). null for a
 * missing or bad value. `today` says whether it falls on now's Eastern day.
 */
export function etWhen(value, now = Date.now(), { sched = false } = {}) {
  const ms = msOf(value);
  if (ms === null) return null;
  const nowMs = msOf(now) ?? Date.now();
  const t = etMomentOf(ms);
  const n = etMomentOf(nowMs);
  const clock = sched && t.minute === "00" ? `${t.hour} ${t.period}` : `${t.hour}:${t.minute} ${t.period}`;
  const diff = t.dayIndex - n.dayIndex;
  if (diff === 0) {
    const day = sched && ms >= nowMs && t.hour24 >= 17 ? "tonight" : "today";
    return { text: `${day} ${clock}`, clock, today: true };
  }
  if (Math.abs(diff) <= 6) return { text: `${t.weekday} ${clock}`, clock, today: false };
  return { text: `${t.month} ${t.day}, ${clock}`, clock, today: false };
}

const WAITING_KINDS = new Set([
  "neither_started",
  "vault",
  "reveal_confirm",
  "reveal",
  "reveal_neither",
  "revealed_unseen",
  "hellos_only",
  "reply",
  "talking",
  "quiet",
]);
// Ended rows carry no waitingOn by contract; this only guards an older or
// confused server, so a closed card never says "Waiting on".
const ENDED_STATUSES = new Set(["expired", "unmatched", "blocked", "qa_parked"]);

// The notice names, as the operator knows them from Settings.
const NOTICE_NAMES = {
  hellos_only_notice: "Hellos only notice",
  quiet_reveal_notice: "Quiet reveal notice",
  still_in_notice: "Still in notice",
};

const sameId = (a, b) => a != null && b != null && String(idOf(a)) === String(idOf(b));
export const firstWord = (s) => String(s || "").trim().split(/\s+/)[0] || "";

/** The row's waitingOn when it is one this file can word (v 1, known kind, live row), else null. */
const usableWaitingOn = (row) => {
  const w = row && row.waitingOn;
  if (!w || typeof w !== "object" || w.v !== 1 || !WAITING_KINDS.has(w.kind)) return null;
  const status = row.compatibilityStatus || row.status;
  if (status && ENDED_STATUSES.has(status)) return null;
  return w;
};

/**
 * What the machine does next, worded two ways from one switch (Introductions
 * board, founder go 2026-10-20): `sentence` is the waiting-on block's one
 * line ("Reminder to Anya tonight 6 PM · closes Wed 6 PM if still quiet"),
 * and `when` + `what` are the same words split for the board's Next column
 * ("tonight 6 PM" over "Reminder to Anya · closes Wed 6 PM if still quiet").
 * One switch, so the two can never drift apart. null when there is no next
 * step, it has no usable time, or the kind is one this file does not know.
 */
function nextStepWords(n, firstOf, at) {
  const when = n ? at(n.at, { sched: true }) : "";
  if (!n || !when) return null;
  const who = n.whoId != null ? firstOf(n.whoId) : "";
  switch (n.kind) {
    case "promise_prompt": {
      // closeAt is an extra field (not in the v 1 contract): when the
      // server sends it, the close the reminder leads to rides along.
      const closeWhen = at(n.closeAt, { sched: true });
      const to = who ? ` to ${who}` : "";
      const close = closeWhen ? ` · closes ${closeWhen} if still quiet` : "";
      return { when, what: `Reminder${to}${close}`, sentence: `Reminder${to} ${when}${close}` };
    }
    case "promise_close":
      return { when, what: "Closes if still quiet", sentence: `Closes ${when} if still quiet` };
    case "both_quiet_close":
    case "stalled_close":
      return {
        when,
        what: "Closes, nobody blamed, if still quiet",
        sentence: `Closes ${when}, nobody blamed, if still quiet`,
      };
    case "expire_unopened":
      return { when, what: "Closes if nobody answers", sentence: `Closes ${when} if nobody answers` };
    case "quiet_card_close": {
      const tail = `if ${who ? `${who} has` : "nobody has"} not opened it`;
      return { when, what: `Quiet card: closes ${tail}`, sentence: `Quiet card: closes ${when} ${tail}` };
    }
    default:
      return NOTICE_NAMES[n.kind]
        ? { when, what: NOTICE_NAMES[n.kind], sentence: `${NOTICE_NAMES[n.kind]} goes ${when}` }
        : null;
  }
}

/**
 * Who each id in a row's waitingOn is (first names only, house rule), shared
 * by waitingOnLines and waitingOnNextParts so both name people the same way.
 */
function waitingNamesOf(row, w) {
  const people = w.people && typeof w.people === "object" ? w.people : {};
  const sides = [row.fromUser || row.userA, row.toUser || row.userB].filter((u) => u && typeof u === "object");
  // Chips and lines go in the row's own order (from, then to), so they line
  // up with the member column; any other id the server named comes after.
  const sideIds = sides.map((u) => String(u._id)).filter((id) => id !== "undefined");
  const peopleIds = Object.keys(people);
  const ids = peopleIds.length
    ? [...sideIds.filter((id) => peopleIds.includes(id)), ...peopleIds.filter((id) => !sideIds.includes(id))]
    : sideIds;
  const personOf = (id) => (id != null && people[String(idOf(id))]) || {};
  // Whether the server sent facts for this member at all. Without an entry
  // nothing is known, so nothing negative ("has not said hello") is said
  // about them (review 2026-10-20: an empty people map made a green
  // "Talking" row claim neither member had said hello).
  const knownOf = (id) => {
    const p = id != null ? people[String(idOf(id))] : null;
    return !!p && typeof p === "object";
  };

  // First names only (house rule), even if the server ever sends a full one.
  const firstOf = (id) => {
    if (id == null) return "one of them";
    const fromPeople = firstWord(personOf(id).first);
    if (fromPeople) return fromPeople;
    const side = sides.find((u) => sameId(u._id, id));
    return (side && firstWord(side.fullName)) || "one of them";
  };
  return { people, sides, ids, personOf, knownOf, firstOf };
}

/**
 * The machine's next step for the board's Next column (founder go
 * 2026-10-20): { at, when, what } with `when` like "tonight 6 PM" and `what`
 * the rest of the waiting-on block's next line, word for word. null when
 * there is none (same rules as waitingOnLines' `next`).
 */
export function waitingOnNextParts(row, now = Date.now()) {
  const w = usableWaitingOn(row);
  if (!w) return null;
  const { firstOf } = waitingNamesOf(row, w);
  const at = (value, opts) => {
    const r = etWhen(value, now, opts);
    return r ? r.text : "";
  };
  const words = nextStepWords(w.next, firstOf, at);
  return words ? { at: w.next.at, when: words.when, what: words.what } : null;
}

/**
 * The lines of the "waiting on" label for one Introductions row, or null
 * when the row has no waitingOn (older server, ended row, unknown kind):
 * { tone, headline, chips: [{ ok, text }], lines: [string], next }.
 * `now` is a Date, an ISO string or ms (tests pin it).
 */
export function waitingOnLines(row, now = Date.now()) {
  const w = usableWaitingOn(row);
  if (!w) return null;
  const { ids, personOf, knownOf, firstOf } = waitingNamesOf(row, w);
  const at = (value, opts) => {
    const r = etWhen(value, now, opts);
    return r ? r.text : "";
  };

  const onId = w.onUserId != null ? String(w.onUserId) : null;
  const on = firstOf(onId);
  const otherId = ids.find((id) => id !== onId) || null;
  const chips = [];
  const lines = [];
  let tone = "warning";
  let headline = "";

  // "Both 9 of 9", or "Gabriel 9 of 9 · Anya 8 of 9".
  const vaultLine = () => {
    const counts = ids.map((id) => personOf(id).vault);
    if (counts.length === 2 && counts.every(Number.isFinite) && counts[0] === counts[1]) {
      return `Both ${counts[0]} of 9`;
    }
    return ids
      .filter((id) => Number.isFinite(personOf(id).vault))
      .map((id) => `${firstOf(id)} ${personOf(id).vault} of 9`)
      .join(" · ");
  };
  // "Anya last in the app Mon 4:30 PM (23 h ago)"; the age only when it was
  // not today, where the clock already says it.
  const lastInAppLine = (id) => {
    const p = personOf(id);
    const when = etWhen(p.lastInApp, now);
    if (!when) return null;
    const age = when.today ? "" : gapLabel(p.lastInApp, now);
    return `${firstOf(id)} last in the app ${when.text}${age ? ` (${age} ago)` : ""}`;
  };
  const pushLine = (line) => {
    if (line) lines.push(line);
  };

  switch (w.kind) {
    case "neither_started": {
      headline = "Neither started";
      // Who has seen it, when the server knows (viewedAt sent, even as null).
      const seen = ids
        .filter((id) => personOf(id).viewedAt !== undefined)
        .map((id) => {
          const when = at(personOf(id).viewedAt);
          return when ? `${firstOf(id)} opened it ${when}` : `${firstOf(id)} has not opened it`;
        });
      if (seen.length) lines.push(seen.join(" · "));
      break;
    }
    case "vault": {
      const p = personOf(onId);
      // A missing count is unknown, not 0: no number then (review 2026-10-20).
      headline = Number.isFinite(p.vault) ? `Waiting on ${on}: Vault ${p.vault} of 9` : `Waiting on ${on} in the Vault`;
      const bits = [];
      if (otherId && Number.isFinite(personOf(otherId).vault)) bits.push(`${firstOf(otherId)} ${personOf(otherId).vault} of 9`);
      const last = at(p.vaultLastAt);
      // "has not answered yet" only when the count says so; answers with no
      // time on them just leave the "last answered" bit out.
      if (last) bits.push(`${on} last answered ${last}`);
      else if (p.vault === 0) bits.push(`${on} has not answered yet`);
      if (bits.length) lines.push(bits.join(" · "));
      // No "last in the app" line here: the approved mock-up's Vault row
      // (MJ + Nicole) has the one line only (review 2026-10-20).
      break;
    }
    case "reveal": {
      headline = `Waiting on ${on} to reveal`;
      const bits = [vaultLine()].filter(Boolean);
      const tap = w.tap && w.tap.at ? etWhen(w.tap.at, now) : null;
      const tapMs = tap ? msOf(w.tap.at) : null;
      if (tap) bits.push(`${firstOf(w.tap.byId)} tapped Reveal ${tap.text}`);
      const p = personOf(onId);
      const viewedMs = msOf(p.viewedAt);
      const told = w.lastTold && sameId(w.lastTold.toId, onId) ? etWhen(w.lastTold.at, now) : null;
      const toldMs = told ? msOf(w.lastTold.at) : null;
      if (viewedMs !== null && (tapMs === null || viewedMs >= tapMs)) {
        bits.push(`${on} opened it ${at(p.viewedAt)}, did not tap`);
      } else if (told && (tapMs === null || toldMs >= tapMs)) {
        // "Jay tapped Reveal today 10:50 AM · Lud told 11:20 AM": a second
        // "today" in one line is noise.
        bits.push(`${on} told ${tap && tap.today && told.today ? told.clock : told.text}, has not opened it`);
      } else {
        bits.push(`${on} has not opened it`);
      }
      lines.push(bits.join(" · "));
      pushLine(lastInAppLine(onId));
      break;
    }
    case "reveal_confirm": {
      const who = w.reconfirmUserId != null ? w.reconfirmUserId : onId;
      headline = `Both tapped Reveal: waiting on ${firstOf(who)} to confirm`;
      const bits = [vaultLine()].filter(Boolean);
      if (w.tap && w.tap.at && at(w.tap.at)) bits.push(`${firstOf(w.tap.byId)} tapped Reveal ${at(w.tap.at)}`);
      if (bits.length) lines.push(bits.join(" · "));
      pushLine(lastInAppLine(who));
      break;
    }
    case "reveal_neither": {
      headline = "Both done: neither has tapped Reveal";
      pushLine(vaultLine());
      ids.forEach((id) => pushLine(lastInAppLine(id)));
      break;
    }
    case "revealed_unseen": {
      const when = at(w.revealedAt);
      // The server sends onUserId null when BOTH have not seen it.
      headline = `Revealed${when ? ` ${when}` : ""}: ${onId ? `${on} has not seen it yet` : "neither has seen it yet"}`;
      if (onId) {
        if (otherId && at(personOf(otherId).sawRevealAt)) {
          lines.push(`${firstOf(otherId)} saw it ${at(personOf(otherId).sawRevealAt)}`);
        }
        pushLine(lastInAppLine(onId));
      } else {
        ids.forEach((id) => pushLine(lastInAppLine(id)));
      }
      break;
    }
    default: {
      // After the reveal: one chip per member, "said hello · then 5 more".
      // Only members the server sent facts for get a chip.
      ids.filter(knownOf).forEach((id) => {
        const p = personOf(id);
        const f = firstOf(id);
        const more = Number.isFinite(p.ownWords) && p.ownWords > 0 ? p.ownWords : 0;
        const then = more ? ` · then ${more} more` : "";
        if (p.helloGone) chips.push({ ok: false, text: `${f}'s hello was removed${then}` });
        else if (p.helloSent) chips.push({ ok: true, text: `${f} said hello${then}` });
        else if (more) chips.push({ ok: true, text: `${f} wrote ${more} message${more === 1 ? "" : "s"}` });
        else chips.push({ ok: false, text: `${f} has not said hello` });
      });
      const lastAt = at(w.lastMessageAt);
      const from = w.lastMessageFromId != null ? `, from ${firstOf(w.lastMessageFromId)}` : "";
      if (w.kind === "hellos_only") {
        const known = ids.filter(knownOf);
        const hellos = known.filter((id) => personOf(id).helloSent && !personOf(id).helloGone);
        // The server's onUserId is null both when both said hello and when
        // neither did (2.4 members, hello not required): one member is
        // named only when onUserId says so or a people entry proves it.
        const missing = onId || (hellos.length === 1 ? known.find((id) => !hellos.includes(id)) : null) || null;
        if (hellos.length >= 2) {
          headline = "Both said hello, nobody has written since";
          tone = "neutral";
        } else if (missing) {
          headline = `Waiting on ${firstOf(missing)} to say hello`;
        } else if (known.length >= 2 && !hellos.length) {
          headline = "Neither has said hello";
        } else {
          headline = "Revealed: nobody has written yet";
        }
        if (lastAt) lines.push(`Last message ${lastAt}${from}`);
      } else if (w.kind === "reply") {
        headline = `Waiting on ${on} to reply`;
        if (lastAt) lines.push(`Last message ${lastAt}${from}`);
      } else if (w.kind === "talking") {
        headline = "Talking";
        tone = "success";
        if (lastAt) lines.push(`Last message ${lastAt}`);
      } else {
        headline = lastAt ? `Quiet since ${lastAt}` : "Quiet";
        tone = "neutral";
      }
      // openedChat comes from a log written once per member per pair, ever,
      // so it reads false on a re-introduced pair and on pairs revealed
      // before 09-05. Said only when nothing else shows they were in the
      // chat: no hello, no words of their own, no last-wrote time (review
      // 2026-10-20: a talking member was said to have never opened it).
      ids.filter(knownOf).forEach((id) => {
        const p = personOf(id);
        const wrote = (Number.isFinite(p.ownWords) && p.ownWords > 0) || !!p.lastWroteAt || p.helloSent || p.helloGone;
        if (p.openedChat === false && !wrote) lines.push(`${firstOf(id)} has not opened the chat yet`);
      });
      break;
    }
  }

  // Who is on a break (a pause of their introductions).
  const onBreak = ids.filter((id) => personOf(id).onBreak === true).map(firstOf);
  if (w.blockedBy !== "on_break" && onBreak.length) {
    lines.push(`${onBreak.join(" and ")} ${onBreak.length === 1 ? "is" : "are"} on a break`);
  }

  // lastTold shows only where the approved mock-up uses it: the reveal
  // line's "told ..., has not opened it". No general "Last notice" line
  // (review 2026-10-20: it added lines the founder did not approve, and on
  // Talking and Quiet rows it could show a weeks-old Vault-stage prompt).

  // What the machine does next, in one short sentence (nextStepWords, the
  // same switch the board's Next column reads).
  const nextWords = nextStepWords(w.next, firstOf, at);
  let next = nextWords ? nextWords.sentence : null;

  // Why the Promise will not close this pair, in plain words.
  if (w.blockedBy === "promise_off") {
    if (next) lines.push("The Promise is off");
    else next = "Nothing scheduled: the Promise is off";
  } else if (w.blockedBy) {
    const reasons = {
      meetup: "they said they have a plan to meet",
      member_gone: "a member has left",
      test_pair: "a test account is in this pair",
      on_break: onBreak.length
        ? `${onBreak.join(" and ")} ${onBreak.length === 1 ? "is" : "are"} on a break`
        : "one of them is on a break",
      quiet_card: "it is a quiet card",
    };
    if (reasons[w.blockedBy]) lines.push(`The Promise will not close this: ${reasons[w.blockedBy]}`);
    if (w.blockedBy === "member_gone") tone = "danger";
  }

  return { tone, headline, chips, lines, next };
}

/**
 * Normalizes a compatibility (or hybrid) API record for display, vault, and chat.
 */
export function normalizeCompatibilityRecord(data) {
  if (!data) return null;

  const fromEmail = resolveFromUserDisplay(data);
  const toEmail = resolveToUserDisplay(data);

  const userAId =
    data.userA?._id != null ? String(data.userA._id) : data.userA != null ? String(data.userA) : "";
  const userBId =
    data.userB?._id != null ? String(data.userB._id) : data.userB != null ? String(data.userB) : "";

  const fromUserObjId =
    data.fromUser && typeof data.fromUser === "object" && data.fromUser._id
      ? String(data.fromUser._id)
      : "";
  const toUserObjId =
    data.toUser && typeof data.toUser === "object" && data.toUser._id
      ? String(data.toUser._id)
      : "";

  const fromUserId = fromUserObjId || (isLikelyObjectId(userAId) ? userAId : "");
  const toUserId = toUserObjId || (isLikelyObjectId(userBId) ? userBId : "");

  return {
    raw: data,
    fromEmail: fromEmail || "N/A",
    toEmail: toEmail || "N/A",
    fromUserId,
    toUserId,
  };
}
